import { useState, useRef, useEffect } from 'react'

export interface SuggestionsState<T> {
  suggestions: T[]
  showSuggestions: boolean
  isLoading: boolean
  isUserTyping: boolean
  selectedIndex: number
  isExpanded: boolean
  suggestionsRef: React.RefObject<HTMLDivElement>
  debounceTimer: React.MutableRefObject<NodeJS.Timeout | null>
}

export interface SuggestionsActions {
  setSuggestions: (suggestions: any[]) => void
  setShowSuggestions: (show: boolean) => void
  setIsLoading: (loading: boolean) => void
  setIsUserTyping: (typing: boolean) => void
  setSelectedIndex: (index: number) => void
  setIsExpanded: (expanded: boolean) => void
  reset: () => void
  hideAll: () => void
}

export function useSuggestions<T>(): [SuggestionsState<T>, SuggestionsActions] {
  const [suggestions, setSuggestions] = useState<T[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isUserTyping, setIsUserTyping] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [isExpanded, setIsExpanded] = useState(false)
  const suggestionsRef = useRef<HTMLDivElement>(null)
  const debounceTimer = useRef<NodeJS.Timeout | null>(null)

  const reset = () => {
    setSuggestions([])
    setShowSuggestions(false)
    setIsExpanded(false)
  }

  const hideAll = () => {
    setShowSuggestions(false)
    setSelectedIndex(-1)
    setIsExpanded(false)
  }

  return [
    {
      suggestions,
      showSuggestions,
      isLoading,
      isUserTyping,
      selectedIndex,
      isExpanded,
      suggestionsRef,
      debounceTimer
    },
    {
      setSuggestions,
      setShowSuggestions,
      setIsLoading,
      setIsUserTyping,
      setSelectedIndex,
      setIsExpanded,
      reset,
      hideAll
    }
  ]
}

export function useClickOutsideAndEscape(
  refs: React.RefObject<HTMLDivElement>[],
  hideCallbacks: (() => void)[],
  showStates: boolean[]
) {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      refs.forEach((ref, index) => {
        if (ref.current && !ref.current.contains(event.target as Node)) {
          hideCallbacks[index]()
        }
      })
    }

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        showStates.forEach((isShown, index) => {
          if (isShown) {
            event.preventDefault()
            hideCallbacks[index]()
          }
        })
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscapeKey)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [...showStates])
}
